import axios from "axios";
import { baseApi } from "@/core/api/baseApi";
import { ApiConfig } from "@/features/auth/infrastructure/config/apiConfig";
import {
  normalizeOtpPreference,
  type OtpPreference,
} from "@/features/auth/domain/constants/otpConstants";
import type {
  UserSettings,
  UserSettingsSnapshot,
} from "../../domain/entities/UserSettings";
import { readTotpEnabledFromRecord } from "@/features/auth/domain/utils/readTotpEnabled";
import type {
  UpdateUserSettingsDTO,
  UserSettingsDataDTO,
  UserSettingsResponseDTO,
} from "../../domain/dto/UserSettingsDTO";

const mapUserSettingsSnapshot = (
  data: UserSettingsDataDTO,
): UserSettingsSnapshot => ({
  otpPreference: normalizeOtpPreference(data.otp_preference) ?? undefined,
  isTotpEnabled:
    readTotpEnabledFromRecord(data as Record<string, unknown>) ?? false,
});

const mapUserSettings = (data: UserSettingsDataDTO): UserSettings => ({
  ...mapUserSettingsSnapshot(data),
  e2eEncryptionEnabled: data.e2e_encryption_enabled ?? false,
  webLanguage: data.web_language
    ? {
        id: data.web_language.id,
        code: data.web_language.code,
        name: data.web_language.name,
      }
    : undefined,
});

export const fetchUserSettings = async (
  token: string,
): Promise<UserSettingsSnapshot> => {
  const response = await axios.get<UserSettingsResponseDTO>(
    ApiConfig.USER_SETTINGS,
    { headers: { Authorization: `Bearer ${token}` } },
  );

  return mapUserSettingsSnapshot(response.data.data);
};

export const patchOtpPreference = async (
  accessToken: string,
  otpPreference: OtpPreference,
): Promise<void> => {
  const body: UpdateUserSettingsDTO = { otp_preference: otpPreference };
  await axios.patch<UserSettingsResponseDTO>(ApiConfig.USER_SETTINGS, body, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export const userSettingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserSettings: builder.query<UserSettings, void>({
      query: () => ({ url: "/users/settings" }),
      transformResponse: (response: UserSettingsResponseDTO) =>
        mapUserSettings(response.data),
      providesTags: ["Profile"],
    }),

    updateUserSettings: builder.mutation<
      UserSettingsDataDTO,
      UpdateUserSettingsDTO
    >({
      query: (body) => ({
        url: "/users/settings",
        method: "PATCH",
        body,
      }),
      transformResponse: (response: UserSettingsResponseDTO) => response.data,
      invalidatesTags: ["Profile"],
    }),
  }),
});

export const { useGetUserSettingsQuery, useUpdateUserSettingsMutation } =
  userSettingsApi;
